"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  AlertCircle,
  CheckCircle2,
  FileSpreadsheet,
  Upload,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import Papa from "papaparse";
import { CitizensAPI } from "@/lib/api";
import { z } from "zod";
import { downloadAndUploadExternalImage } from "@/lib/utils";

interface CitizenImport {
  citizen_name: string;
  citizen_bio_short?: string;
  citizen_bio?: string;
  slug: string;
  tags?: string[];
  main_pic?: string;
  profile_pic?: string;
  banner_pic?: string;
  twitter_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  website_url?: string;
  country_iso_code?: string;
  is_verified?: boolean;
  is_active?: boolean;
  citizen_ratings?: {
    personality_score?: number;
    likability_score?: number;
    professionalism_score?: number;
    influence_score?: number;
    skill_score?: number;
    social_responsibility_score?: number;
    clout_score?: number;
    political_spectrum_score?: number;
  };
}

interface ValidateResult {
  citizen_name: string;
  citizen_slug: string;
  exists: boolean;
  existing_id?: number;
  name_exists?: boolean;
  slug_exists?: boolean;
}

interface ValidationResponse {
  results: ValidateResult[];
  total: number;
  existing_count: number;
}

interface ImportResponse {
  message: string;
  failures?: {
    error: string;
    index: number;
    citizen: string | null;
  }[];
  failure_count: number;
  success_count: number;
}

// Add this validation schema to the top of your file
const citizenRowSchema = z.object({
  citizen_name: z.string().min(1, { message: "Citizen name is required" }),
  slug: z.string().min(1, { message: "Slug is required" }),
  citizen_bio_short: z.string().optional(),
  citizen_bio: z.string().optional(),
  // Add other fields as needed
});

export default function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [citizens, setCitizens] = useState<CitizenImport[]>([]);
  const [duplicates, setDuplicates] = useState<ValidateResult[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>("");
  const [isProcessingImages, setIsProcessingImages] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setErrors([]);
      setDuplicates([]);
      setSuccess(false);
    } else {
      toast.error("Please select a valid CSV file");
      setFile(null);
    }
  };

  const validateCitizens = async (
    parsedCitizens: CitizenImport[]
  ): Promise<ValidationResponse> => {
    try {
      const validationPayload = parsedCitizens.map((citizen) => ({
        citizen_name: citizen.citizen_name,
        citizen_slug: citizen.slug,
      }));

      const response = await CitizensAPI.validateBulk(validationPayload);
      return response;
    } catch (error) {
      console.error("Validation error:", error);
      return { results: [], total: parsedCitizens.length, existing_count: 0 };
    }
  };

  const processCSV = async () => {
    if (!file) return;

    setIsProcessing(true);
    setProgress(10);
    setProgressMessage("Reading CSV file...");
    setErrors([]);
    setDuplicates([]);
    setIsProcessingImages(false);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        setProgress(30);
        setProgressMessage("Validating data format...");
        const parsedData = results.data as Record<string, string>[];
        const newErrors: string[] = [];
        const importedCitizens: CitizenImport[] = [];
        const skippedRows: number[] = [];

        // Track image processing stats
        let totalImages = 0;
        let processedImages = 0;
        let imageProcessingErrors = 0;

        // Process each row in the CSV file
        for (let index = 0; index < parsedData.length; index++) {
          const row = parsedData[index];
          setProgressMessage(
            `Processing row ${index + 1} of ${parsedData.length}...`
          );

          // Generate slug from name if missing
          if (!row.slug && row.citizen_name) {
            row.slug = row.citizen_name
              .toLowerCase()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-");
          }

          // Validate required fields using Zod
          const validationResult = citizenRowSchema.safeParse(row);
          if (!validationResult.success) {
            const formattedErrors = validationResult.error.format();

            // Use a simpler approach with proper type handling
            const errorMessages: string[] = [];

            // Process each field's errors
            Object.entries(formattedErrors).forEach(([field, error]) => {
              // Skip the _errors top-level property
              if (field !== "_errors" && error && "_errors" in error) {
                // Extract the first error message for this field
                const errorMessage = error._errors[0];
                if (errorMessage) {
                  errorMessages.push(`${field}: ${errorMessage}`);
                }
              }
            });

            const errorSummary = errorMessages.join(", ");
            newErrors.push(
              `Row ${index + 1}: Validation failed - ${errorSummary}`
            );
            skippedRows.push(index + 1);
            continue;
          }

          // Parse tags using pipe delimiter
          const tags = row.tags
            ? row.tags.split("|").map((tag) => tag.trim())
            : [];

          // Parse ratings
          const ratings: Record<string, number> = {};
          const ratingFields = [
            "personality_score",
            "likability_score",
            "professionalism_score",
            "influence_score",
            "skill_score",
            "social_responsibility_score",
            "clout_score",
            "political_spectrum_score",
          ];

          ratingFields.forEach((field) => {
            if (row[field]) {
              const score = parseFloat(row[field]);
              if (!isNaN(score)) {
                ratings[field] = score;
              }
            }
          });

          // Process and download external images if needed
          let mainPicUrl = row.main_pic
            ? decodeURIComponent(row.main_pic)
            : undefined;
          let profilePicUrl = row.profile_pic
            ? decodeURIComponent(row.profile_pic)
            : undefined;
          let bannerPicUrl = row.banner_pic
            ? decodeURIComponent(row.banner_pic)
            : undefined;

          const hasImages = mainPicUrl || profilePicUrl || bannerPicUrl;
          if (hasImages) {
            totalImages +=
              (mainPicUrl ? 1 : 0) +
              (profilePicUrl ? 1 : 0) +
              (bannerPicUrl ? 1 : 0);
            setIsProcessingImages(true);
          }

          try {
            // Check and process images if they exist
            if (mainPicUrl) {
              setProgressMessage(
                `Processing image for ${row.citizen_name} (main)...`
              );
              const newUrl = await downloadAndUploadExternalImage(
                mainPicUrl,
                "main",
                row.slug
              );
              if (newUrl !== mainPicUrl) {
                processedImages++;
                mainPicUrl = newUrl;
              } else if (mainPicUrl.startsWith("http")) {
                imageProcessingErrors++;
              }
            }

            if (profilePicUrl) {
              setProgressMessage(
                `Processing image for ${row.citizen_name} (profile)...`
              );
              const newUrl = await downloadAndUploadExternalImage(
                profilePicUrl,
                "profile",
                row.slug
              );
              if (newUrl !== profilePicUrl) {
                processedImages++;
                profilePicUrl = newUrl;
              } else if (profilePicUrl.startsWith("http")) {
                imageProcessingErrors++;
              }
            }

            if (bannerPicUrl) {
              setProgressMessage(
                `Processing image for ${row.citizen_name} (banner)...`
              );
              const newUrl = await downloadAndUploadExternalImage(
                bannerPicUrl,
                "banner",
                row.slug
              );
              if (newUrl !== bannerPicUrl) {
                processedImages++;
                bannerPicUrl = newUrl;
              } else if (bannerPicUrl.startsWith("http")) {
                imageProcessingErrors++;
              }
            }
          } catch (error) {
            console.error(
              `Error processing images for row ${index + 1}:`,
              error
            );
            imageProcessingErrors++;
            // Continue with the original URLs if there was an error
          }

          // Build citizen object
          const citizen: CitizenImport = {
            citizen_name: row.citizen_name,
            slug: row.slug,
            citizen_bio_short: row.citizen_bio_short || undefined,
            citizen_bio: row.citizen_bio || undefined,
            tags: tags.length > 0 ? tags : undefined,
            main_pic: mainPicUrl,
            profile_pic: profilePicUrl,
            banner_pic: bannerPicUrl,
            twitter_url: row.twitter_url || undefined,
            facebook_url: row.facebook_url || undefined,
            instagram_url: row.instagram_url || undefined,
            tiktok_url: row.tiktok_url || undefined,
            youtube_url: row.youtube_url || undefined,
            website_url: row.website_url || undefined,
            country_iso_code: row.country_iso_code || undefined,
            is_verified: row.is_verified?.toLowerCase() === "true",
            is_active: row.is_active?.toLowerCase() === "true",
          };

          if (Object.keys(ratings).length > 0) {
            citizen.citizen_ratings = ratings;
          }

          importedCitizens.push(citizen);
        }

        if (isProcessingImages) {
          if (imageProcessingErrors > 0) {
            toast.warning(
              `${imageProcessingErrors} images could not be processed and will use original URLs`
            );
          }
          if (processedImages > 0) {
            toast.success(`Successfully processed ${processedImages} images`);
          }
          setIsProcessingImages(false);
        }

        if (skippedRows.length > 0) {
          toast.warning(
            `Skipped ${skippedRows.length} rows due to missing or invalid data`
          );
        }

        if (newErrors.length > 0) {
          setErrors(newErrors);

          if (importedCitizens.length === 0) {
            setProgress(0);
            setProgressMessage("");
            toast.error(
              `Found ${newErrors.length} errors in CSV. No valid rows found.`
            );
            setIsProcessing(false);
            return;
          }

          // If we have some valid rows, continue processing but show warnings
          toast.warning(`Found ${newErrors.length} validation errors in CSV`);
        }

        if (importedCitizens.length === 0) {
          setProgress(0);
          setProgressMessage("");
          toast.error("No valid citizens found in CSV");
          setIsProcessing(false);
          return;
        }

        // Check for duplicates
        setProgress(50);
        setProgressMessage("Checking for duplicate citizens...");
        const validationResponse = await validateCitizens(importedCitizens);
        setProgress(70);
        setProgressMessage("Finalizing import data...");

        if (validationResponse.existing_count > 0) {
          setDuplicates(validationResponse.results);

          // Filter out duplicates from the citizens to be imported
          const uniqueCitizens = importedCitizens.filter((citizen) => {
            return !validationResponse.results.some(
              (duplicate) =>
                duplicate.citizen_name === citizen.citizen_name ||
                duplicate.citizen_slug === citizen.slug
            );
          });

          setCitizens(uniqueCitizens);

          if (uniqueCitizens.length === 0) {
            toast.error("All valid citizens already exist in the system");
            setProgress(0);
            setProgressMessage("");
          } else {
            toast.warning(
              `Found ${validationResponse.existing_count} duplicate citizens that will be skipped`
            );
            setProgress(100);
            setProgressMessage("Ready for upload");
            toast.success(
              `Successfully parsed ${uniqueCitizens.length} unique citizens`
            );
          }
        } else {
          setCitizens(importedCitizens);
          setProgress(100);
          setProgressMessage("Ready for upload");
          toast.success(
            `Successfully parsed ${importedCitizens.length} citizens`
          );
        }

        setIsProcessing(false);
      },
      error: (error) => {
        setErrors([`Error parsing CSV: ${error.message}`]);
        setIsProcessing(false);
        setProgress(0);
        setProgressMessage("");
        toast.error("Failed to parse CSV file");
      },
    });
  };

  const uploadCitizens = async () => {
    if (citizens.length === 0) {
      toast.error("No citizens to upload");
      return;
    }

    setIsUploading(true);
    setProgress(25);
    setProgressMessage("Uploading citizens to the server...");

    try {
      const result = await CitizensAPI.bulkImport(citizens);
      setProgress(100);
      setProgressMessage("Upload complete!");
      setSuccess(true);

      // Handle the response with possible failures
      const importResponse = result as ImportResponse;

      if (importResponse.success_count > 0) {
        toast.success(
          `Successfully imported ${importResponse.success_count} citizens`
        );
      }

      if (importResponse.failure_count > 0) {
        setErrors(
          importResponse.failures?.map(
            (failure) => `Row ${failure.index + 1}: ${failure.error}`
          ) || [`Failed to import ${importResponse.failure_count} citizens`]
        );

        toast.error(
          `Failed to import ${importResponse.failure_count} citizens. See details below.`
        );
      } else {
        // If no failures, reset errors
        setErrors([]);
      }

      // Reset file input
      setFile(null);
      if (importResponse.success_count > 0) {
        // Only clear citizens if something was successful
        setCitizens([]);
      } else {
        // If all failed, keep the citizens data for potential fixes
        toast.error(
          "All imports failed. Please correct the errors and try again."
        );
      }
      setDuplicates([]);

      const fileInput = document.getElementById(
        "file-upload"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast.error(
        `Upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setErrors([
        `Upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      ]);
      setProgressMessage("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const headers = [
      "citizen_name",
      "citizen_bio_short",
      "citizen_bio",
      "slug",
      "tags",
      "main_pic",
      "profile_pic",
      "banner_pic",
      "twitter_url",
      "facebook_url",
      "instagram_url",
      "tiktok_url",
      "youtube_url",
      "website_url",
      "country_iso_code",
      "is_verified",
      "is_active",
      "personality_score",
      "likability_score",
      "professionalism_score",
      "influence_score",
      "skill_score",
      "social_responsibility_score",
      "clout_score",
      "political_spectrum_score",
    ];

    const rows = [
      [
        "John Doe",
        "Famous actor and director",
        "John Doe is a renowned actor and film director.",
        "john-doe",
        "Actor|Director",
        "https://example.com/john.jpg",
        "",
        "",
        "https://twitter.com/johndoe",
        "",
        "https://instagram.com/johndoe",
        "",
        "",
        "https://johndoe.com",
        "US",
        "true",
        "true",
        "8.5",
        "7.9",
        "9.0",
        "8.2",
        "9.5",
        "7.0",
        "8.8",
        "3",
      ],
      [
        "Jane Smith",
        "Renowned painter",
        "Jane Smith is a world-famous artist known for her unique style.",
        "jane-smith",
        "Artist|Painter",
        "https://example.com/jane.jpg",
        "https://example.com/jane-profile.jpg",
        "https://example.com/jane-banner.jpg",
        "",
        "https://facebook.com/janesmith",
        "https://instagram.com/janesmith",
        "",
        "",
        "https://janesmith.com",
        "UK",
        "true",
        "true",
        "7.5",
        "8.9",
        "9.2",
        "7.5",
        "9.8",
        "8.0",
        "7.5",
        "2",
      ],
    ];

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "citizens_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-8">Bulk Import Citizens</h1>

      <div className="grid grid-cols-1 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Upload CSV File</CardTitle>
            <CardDescription>
              Import multiple citizens at once using a CSV file with the proper
              format.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <FileSpreadsheet className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">Choose a CSV file</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  CSV file should include required fields like citizen_name and
                  other optional fields
                </p>
                <Input
                  id="file-upload"
                  type="file"
                  onChange={handleFileChange}
                  accept=".csv"
                  className="hidden"
                />
                <Button
                  onClick={() =>
                    document.getElementById("file-upload")?.click()
                  }
                  className="mx-auto"
                >
                  Select File
                </Button>
              </div>

              {file && (
                <div className="flex items-center p-4 border rounded-lg bg-muted/50">
                  <FileSpreadsheet className="h-6 w-6 mr-2" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={processCSV}
                    disabled={isProcessing || isUploading}
                  >
                    Process
                  </Button>
                </div>
              )}

              {(isProcessing || isUploading || progress > 0) && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      {isProcessing
                        ? "Processing"
                        : isUploading
                        ? "Uploading"
                        : "Complete"}
                      {isProcessingImages && " (downloading images...)"}
                    </span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} />
                  {progressMessage && (
                    <p className="text-xs text-muted-foreground">
                      {progressMessage}
                    </p>
                  )}
                </div>
              )}

              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error in CSV file</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc ml-4 mt-2 text-sm">
                      {errors.slice(0, 5).map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                      {errors.length > 5 && (
                        <li>...and {errors.length - 5} more errors</li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {duplicates.length > 0 && (
                <Alert
                  variant="default"
                  className="bg-amber-50 border-amber-200 text-amber-800"
                >
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Duplicate Citizens Detected</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">
                      {duplicates.length} citizens already exist in the system
                      and will be skipped:
                    </p>
                    <ul className="list-disc ml-4 mt-2 text-sm max-h-32 overflow-y-auto">
                      {duplicates.map((dup, i) => (
                        <li key={i}>
                          {dup.citizen_name} ({dup.citizen_slug})
                          {dup.existing_id && (
                            <span className="text-xs">
                              {" "}
                              - ID: {dup.existing_id}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {citizens.length > 0 && !errors.length && (
                <Alert className="bg-muted">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>CSV Processed Successfully</AlertTitle>
                  <AlertDescription>
                    Processed {citizens.length} citizens from the CSV file. You
                    can now upload them to the system.
                    {duplicates.length > 0 && (
                      <p className="mt-1 text-sm">
                        {duplicates.length} duplicate citizens were excluded
                        from upload.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert
                  className={
                    errors.length > 0
                      ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                      : "bg-green-50 text-green-800 border-green-200"
                  }
                >
                  <CheckCircle2
                    className={`h-4 w-4 ${
                      errors.length > 0 ? "text-yellow-600" : "text-green-600"
                    }`}
                  />
                  <AlertTitle>
                    {errors.length > 0
                      ? "Import Partially Successful"
                      : "Import Successful"}
                  </AlertTitle>
                  <AlertDescription>
                    {errors.length > 0 ? (
                      <div>
                        <p>
                          Some citizens were successfully imported, but others
                          failed.
                        </p>
                        <div className="mt-2">
                          <p className="font-medium">Failed imports:</p>
                          <ul className="list-disc ml-4 mt-1 text-sm">
                            {errors.slice(0, 5).map((error, i) => (
                              <li key={i}>{error}</li>
                            ))}
                            {errors.length > 5 && (
                              <li>...and {errors.length - 5} more errors</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      "Citizens have been successfully imported to the system."
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={downloadSampleCSV}>
              Download Sample CSV
            </Button>
            <Button
              variant="default"
              disabled={!citizens.length || isUploading}
              onClick={uploadCitizens}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading..." : "Upload Citizens"}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Instructions</CardTitle>
            <CardDescription>
              How to prepare your CSV file for import
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm space-y-4">
            <div>
              <h3 className="font-medium mb-1">Required Fields:</h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <code className="bg-muted px-1 rounded">citizen_name</code>:
                  Full name of the citizen
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-1">Common Fields:</h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <code className="bg-muted px-1 rounded">
                    citizen_bio_short
                  </code>
                  : Brief description (max 200 chars)
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">citizen_bio</code>:
                  Full biography (max 500 chars)
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">slug</code>:
                  URL-friendly identifier (generated automatically if empty)
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">tags</code>:
                  Pipe-separated list of tags (e.g., "Actor|Director")
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">
                    country_iso_code
                  </code>
                  : ISO country code (e.g., US, UK)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-1">Images:</h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <code className="bg-muted px-1 rounded">profile_pic</code>:
                  URL for profile picture
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">main_pic</code>: URL
                  for main picture
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">banner_pic</code>: URL
                  for banner picture
                </li>
                <li className="text-emerald-700 font-medium mt-2">
                  <span className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    External images are automatically downloaded and stored in
                    our system
                  </span>
                  <span className="text-xs ml-5">
                    We only process .jpg, .jpeg, and .png images, up to 3MB in
                    size
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-1">Ratings (0-10 scale):</h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <code className="bg-muted px-1 rounded">
                    personality_score
                  </code>
                  : Personality rating
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">
                    likability_score
                  </code>
                  : Likability rating
                </li>
                <li>
                  <code className="bg-muted px-1 rounded">
                    professionalism_score
                  </code>
                  : Professionalism rating
                </li>
                <li>and other rating fields...</li>
              </ul>
            </div>

            <p className="text-muted-foreground">
              Download the sample CSV for a complete template with all available
              fields.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
